import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { NavLink } from "@/components/ui/nav-link";
import { cn } from "@/lib/utils";
import { icons, LucideMenu } from "lucide-react";
import { useIntersectionObserver } from "usehooks-ts";
import type { LinkType } from "./shared";

export interface HeaderLinkType extends LinkType {
  active?: boolean;
}

export function LocationHeader({
  links,
  destination,
}: {
  links: HeaderLinkType[];
  destination: string;
}) {
  const { isIntersecting, ref } = useIntersectionObserver({
    initialIsIntersecting: true,
  });

  return (
    <>
      <header
        className={cn(
          "h-[4.4rem] text-background transition-all sticky top-0 z-50 duration-300 bg-background",
          {
            "border-b": !isIntersecting,
          },
        )}
      >
        <div className="px-4 max-w-screen-xl mx-auto flex justify-between items-center h-full">
          <NavLink
            href="/"
            className="hover:opacity-30 transition-opacity flex gap-2 items-center"
          >
            <div className="font-bold text-foreground text-2xl tracking-tighter">
              what<span className="text-yellow-700">do</span>
            </div>
          </NavLink>
          <div className="contents xl:hidden">
            <MobileNav links={links} destination={destination} />
          </div>
          <div className="hidden xl:contents">
            <DesktopNav links={links} />
          </div>
        </div>
      </header>
      <div ref={ref} className="h-[1px] bg-transparent absolute top-[4.1rem]" />
    </>
  );
}

const DesktopNav = ({ links }: { links: HeaderLinkType[] }) => (
  <nav className="text-[13px] text-foreground tracking-[0.015em] flex gap-5 sm:gap-8">
    {links.map(({ label, className, iconName, active, ...props }) => (
      <NavLink
        key={label}
        className={cn(
          "underline-offset-8 decoration-2 decoration-yellow-400 hover:underline",
          active && "underline",
          className,
        )}
        {...props}
        href={props.href || "/"}
      >
        {label}
      </NavLink>
    ))}
  </nav>
);

const MobileNav = ({
  links,
  destination,
}: {
  links: HeaderLinkType[];
  destination: string;
}) => (
  <Sheet>
    <SheetTrigger className="p-4 -m-4" aria-label="Menu">
      <LucideMenu className="text-foreground" size={26} />
    </SheetTrigger>
    <SheetContent className="flex flex-col justify-between overflow-y-scroll">
      <SheetHeader>
        <SheetTitle>{destination}</SheetTitle>
        <div className="pt-4 flex flex-col gap-2">
          {links.map((link) => (
            <SheetCloseButton key={link.label} {...link} />
          ))}
        </div>
      </SheetHeader>
      <SheetFooter className="mx-auto">
        <a
          className="text-[11px] transition-opacity hover:opacity-40"
          href="https://9.agency"
          target="_blank"
          rel="noopener nofollow"
        >
          Made by 9.agency
        </a>
      </SheetFooter>
    </SheetContent>
  </Sheet>
);

const SheetCloseButton = ({
  label,
  iconName,
  active,
  ...props
}: HeaderLinkType) => {
  const Icon = icons[iconName];
  return (
    <SheetClose asChild>
      <Button
        variant="shadow"
        asChild
        className={cn("flex gap-2", active && "bg-yellow-300/60")}
      >
        <NavLink {...props} href={props.href || "/"}>
          <Icon height={16} />
          {label}
        </NavLink>
      </Button>
    </SheetClose>
  );
};
